import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { api_url } from "../config/config";
import { toast } from "react-hot-toast";

/* ─── Helpers ─────────────────────────────────────────── */
const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const fmtTime = (d) =>
  d
    ? new Date(d).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const getMrp = (v) => parseFloat(v?.mrp) || parseFloat(v?.price) || 0;

const getFinal = (v) => {
  const d = parseFloat(v?.discounted_price);
  const m = getMrp(v);
  return d > 0 && d < m ? d : m;
};

const STATUS_META = {
  ORDER_PLACED: {
    label: "Order Placed",
    icon: "📋",
    desc: "We've received your order",
  },
  ORDER_PROCESSING: {
    label: "Processing",
    icon: "⚙️",
    desc: "Preparing your medicines",
  },
  OUT_FOR_DELIVERY: {
    label: "Out for Delivery",
    icon: "🚚",
    desc: "On the way to you",
  },
  DELIVERED: {
    label: "Delivered",
    icon: "🏠",
    desc: "Order delivered successfully",
  },
  CANCELLED: {
    label: "Cancelled",
    icon: "✕",
    desc: "Order has been cancelled",
  },
};

const CANCEL_REASONS = [
  "I want to modify items in my order",
  "Bought items from elsewhere",
  "Delay in order",
  "Forgot to apply coupon",
  "Payment related issues",
  "Wallet balance issue",
  "Incorrect address",
  "Other",
];

/* ─── Sub-components ──────────────────────────────────── */
const Pill = ({ children, color = "teal" }) => {
  const cls = {
    teal: "bg-[#ddf0ee] text-[#1e4a46] border-[#aac9c7]",
    orange: "bg-[#fff3ee] text-[#ff601e] border-[#ffd5c2]",
    red: "bg-red-50   text-red-600   border-red-200",
    green: "bg-green-50 text-green-700 border-green-200",
    gray: "bg-[#f2f8f7] text-[#5a7a77] border-[#c8d8d7]",
  }[color];
  return (
    <span
      className={`inline-flex items-center text-[12px] font-bold px-2.5 py-0.5 rounded-full border ${cls}`}
    >
      {children}
    </span>
  );
};

const InfoRow = ({ label, value, mono = false }) => (
  <div className="flex items-center justify-between py-2 border-b border-[#f1f5f5] last:border-0">
    <span className="text-[13px] text-[#5a7a77] font-medium">{label}</span>
    <span
      className={`text-[14px] font-bold text-[#1e3330] ${mono ? "font-mono tracking-wide" : ""}`}
    >
      {value || "—"}
    </span>
  </div>
);

/* ─── Main Component ───────────────────────────────────── */
const Orderdetails = () => {
  const { order_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const { orderDetails } = location.state || {};

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [mounted, setMounted] = useState(false);

  const BASE_STATUSES = [
    "ORDER_PLACED",
    "ORDER_PROCESSING",
    "OUT_FOR_DELIVERY",
    "DELIVERED",
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setMounted(true), 80);
  }, []);

  useEffect(() => {
    if (!orderDetails?.razorpay_order_id) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${api_url}/order/${orderDetails.razorpay_order_id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setOrder(data?.data || null);
      } catch (e) {
        console.error("Error fetching order:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ── Cancel order ── */
  const handleCancel = async () => {
    if (!selectedReason) {
      setCancelError("Please select a reason.");
      return;
    }
    setCancelling(true);
    try {
      await axios.patch(
        `${api_url}/order/${orderDetails.razorpay_order_id}`,
        {
          previous_status: order.delivery_status,
          delivery_status: "CANCELLED",
          reason: selectedReason,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setOrder((p) => ({
        ...p,
        delivery_status: "CANCELLED",
        cancellation_reason: selectedReason,
      }));
      setIsModalOpen(false);
      toast.success("Order cancelled successfully.");
    } catch (e) {
      toast.error("Failed to cancel order. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  /* ── Product click ── */
  const handleProductClick = async (product_id, product_name) => {
    try {
      const res = await fetch(`${api_url}/product/${product_id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-auth": "false",
        },
      });
      const data = await res.json();
      if (data.error) {
        toast.error("Product not available.");
        return;
      }
      navigate(
        `/products/${product_name?.toLowerCase().replace(/\s+/g, "-")}`,
        { state: { product_id } },
      );
    } catch {
      toast.error("Error occurred. Please try again.");
    }
  };

  /* ── Status tracker logic ── */
  const isCancelled = order?.delivery_status === "CANCELLED";
  const currentStatus = order?.delivery_status || "ORDER_PLACED";
  const currentIdx = BASE_STATUSES.indexOf(
    isCancelled ? order?.previous_status || "ORDER_PLACED" : currentStatus,
  );

  /* ── Products list ── */
  const products =
    Array.isArray(order?.product) && order.product.length
      ? order.product
      : orderDetails?.details?.[0]?.product || [];

  const getImage = (item) =>
    item?.variant?.product?.product_images?.image_url?.primary_image ||
    item?.variant?.product?.product_images?.[0]?.image_url?.primary_image ||
    "/default.svg";

  const canCancel =
    !isCancelled &&
    currentStatus !== "DELIVERED" &&
    currentStatus !== "OUT_FOR_DELIVERY";

  /* ════════════════════════════════════════════════════════════ */
  return (
    <div
      className="min-h-screen bg-[#f2f8f7] font-poppins"
    >
      {/* ── Page header ── */}
      <div
        className="relative px-4 sm:px-6 lg:px-12 py-7 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg,#0a1715 0%,#162c2a 55%,#1e4a46 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
            backgroundSize: "40px 40px",
          }}
        />
        <div className=" mx-auto">
          <nav className="flex items-center gap-2 text-[13px] text-white/40 mb-4">
            <Link
              to="/home"
              className="hover:text-white/70 transition-colors"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              Home
            </Link>
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 12l4-4-4-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Link
              to="/my-orders"
              className="hover:text-white/70 transition-colors"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              My Orders
            </Link>
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 12l4-4-4-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-white font-semibold">Order Details</span>
          </nav>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1
                className="text-[23px] sm:text-[27px] font-black text-white leading-tight"
                style={{ fontFamily: "'Nunito',sans-serif" }}
              >
                Order Details
              </h1>
              {order?.orderId && (
                <p className="text-[14px] text-white/55 mt-1 font-mono tracking-wide">
                  {order.orderId}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2.5">
              {isCancelled ? (
                <Pill color="red">✕ Cancelled</Pill>
              ) : currentStatus === "DELIVERED" ? (
                <Pill color="green">✓ Delivered</Pill>
              ) : (
                <Pill color="orange">● In Progress</Pill>
              )}
              {order?.created_at && (
                <span className="text-[13px] text-white/45 bg-white/[0.07] border border-white/[0.12] rounded-xl px-3 py-1">
                  {fmt(order.created_at)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-[#f2f8f7] to-transparent" />
      </div>

      {/* ── Main content ── */}
      <div
        className=" mx-auto px-4 sm:px-6 lg:px-12 py-8 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(16px)",
          transition: "all .5s ease",
        }}
      >
        {/* ════ LEFT COLUMN ════ */}
        <div className="flex flex-col gap-5">
          <div
            className="bg-white rounded-2xl border-[1.5px] border-[#c8d8d7] overflow-hidden"
            style={{ boxShadow: "0 2px 16px rgba(22,44,42,0.07)" }}
          >
            <div className="bg-[#f2f8f7] border-b border-[#c8d8d7] px-5 py-3.5 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-[8px] bg-[#ddf0ee] border border-[#aac9c7] flex items-center justify-center flex-shrink-0">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1e4a46"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </div>
              <span className="text-[14px] font-black text-[#1e4a46]">
                Bill Summary
              </span>
            </div>
            <div className="px-5 py-4">
              {order?.bill?.length > 0 ? (
                order.bill.map((b, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <div className="flex justify-between items-center py-1.5 border-b border-[#f1f5f5]">
                      <span className="text-[13px] text-[#5a7a77] font-medium">
                        Total MRP
                      </span>
                      <span className="text-[14px] font-bold text-[#1e3330]">
                        ₹{b.mrp}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-b border-[#f1f5f5]">
                      <span className="text-[13px] text-[#5a7a77] font-medium">
                        Discount
                      </span>
                      <span className="text-[14px] font-bold text-[#059669]">
                        − ₹{b.discount}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-b border-[#f1f5f5]">
                      <span className="text-[13px] text-[#5a7a77] font-medium">
                        Delivery
                      </span>
                      <span className="text-[14px] font-bold text-[#059669]">
                        Free
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 mt-1">
                      <span className="text-[15px] font-black text-[#0a1715]">
                        Amount Paid
                      </span>
                      <span
                        className="text-[19px] font-black text-[#1e4a46]"
                        style={{ fontFamily: "'Nunito',sans-serif" }}
                      >
                        ₹{b.totalAmount}
                      </span>
                    </div>
                    {/* Savings pill */}
                    {parseFloat(b.discount) > 0 && (
                      <div className="flex items-center gap-2 bg-[#ddf0ee] border border-[#aac9c7] rounded-xl px-3.5 py-2.5 mt-1">
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#1e4a46"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span className="text-[13px] font-bold text-[#1e4a46]">
                          You saved ₹{b.discount} on this order!
                        </span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-[14px] text-[#5a7a77] py-4 text-center">
                  Bill details not available.
                </p>
              )}
            </div>
          </div>
          {/* ── Delivery Status Tracker ── */}
          <div
            className="bg-white rounded-2xl border-[1.5px] border-[#c8d8d7] overflow-hidden"
            style={{ boxShadow: "0 2px_16px rgba(22,44,42,0.07)" }}
          >
            <div className="bg-[#f2f8f7] border-b border-[#c8d8d7] px-5 py-3.5 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-[8px] bg-[#ddf0ee] border border-[#aac9c7] flex items-center justify-center flex-shrink-0">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1e4a46"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 3h15v13H1z" />
                  <path d="M16 8h4l3 3v5h-7V8z" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
              </div>
              <span className="text-[14px] font-black text-[#1e4a46]">
                Delivery Status
              </span>
              {isCancelled && order?.cancellation_reason && (
                <span className="ml-auto text-[12px] text-red-500 font-medium bg-red-50 border border-red-100 rounded-full px-2.5 py-0.5">
                  Reason: {order.cancellation_reason}
                </span>
              )}
            </div>
  <div className="flex items-center justify-center gap-2 bg-[#f2f8f7] border border-[#c8d8d7] rounded-xl px-4 py-3">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span className="text-[11.5px] font-bold text-[#2d5250]">100% Secure Payments</span>
          </div>
            <div className="px-6 py-7">
              {/* Desktop tracker */}
              <div className="hidden sm:flex items-start justify-between relative">
                {/* Base line */}
                <div className="absolute top-[18px] left-[40px] right-[40px] h-[2px] bg-[#dde9e8]" />
                {/* Progress line */}
                <div
                  className="absolute top-[18px] left-[40px] h-[2px] bg-[#1e4a46] transition-all duration-1000"
                  style={{
                    width: isCancelled
                      ? `${(currentIdx / (BASE_STATUSES.length - 1)) * 100 * (1 - 2 / (BASE_STATUSES.length * 10))}%`
                      : currentIdx === BASE_STATUSES.length - 1
                        ? "calc(100% - 80px)"
                        : `${(currentIdx / (BASE_STATUSES.length - 1)) * (100 - 80 / BASE_STATUSES.length)}%`,
                  }}
                />

                {BASE_STATUSES.map((status, i) => {
                  const meta = STATUS_META[status];
                  const done = i <= currentIdx;
                  const isActive = !isCancelled && i === currentIdx;
                  const wasCancelHere = isCancelled && i === currentIdx;

                  return (
                    <div
                      key={status}
                      className="flex flex-col items-center gap-2 z-10 flex-1 relative"
                    >
                      {/* Icon circle */}
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center text-[16px] border-[1.5px] transition-all duration-500 relative overflow-hidden
                          ${
                            wasCancelHere
                              ? "bg-red-500 border-red-500 text-white"
                              : done
                                ? "bg-[#1e4a46] border-[#1e4a46] text-white"
                                : "bg-white border-[#c8d8d7] text-[#aac9c7]"
                          }`}
                        style={
                          isActive
                            ? { boxShadow: "0 4px 14px rgba(22,44,42,0.28)" }
                            : {}
                        }
                      >
                        {/* Active pulse */}
                        {isActive && (
                          <span
                            className="absolute inset-0 rounded-xl bg-[#1e4a46] opacity-20"
                            style={{
                              animation:
                                "stepPing 1.4s cubic-bezier(0,0,.2,1) infinite",
                            }}
                          />
                        )}
                        {wasCancelHere ? (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="white"
                          >
                            <path d="M9.43 7L13.5 2.94C14.17 2.26 14.17 1.18 13.5.51 12.82-.17 11.74-.17 11.07.51L7 4.57 2.93.5C2.26-.17 1.17-.17.5.5c-.67.67-.67 1.76 0 2.43L4.57 7 .5 11.07c-.67.67-.67 1.76 0 2.43.67.67 1.76.67 2.43 0L7 9.43l4.07 4.07c.67.67 1.76.67 2.43 0 .67-.67.67-1.76 0-2.43L9.43 7z" />
                          </svg>
                        ) : done ? (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <span className="text-[14px]">{meta.icon}</span>
                        )}
                      </div>

                      {/* Label */}
                      <div className="text-center">
                        <p
                          className={`text-[12px] font-bold leading-tight ${done && !wasCancelHere ? "text-[#1e4a46]" : wasCancelHere ? "text-red-500" : "text-[#aac9c7]"}`}
                        >
                          {meta.label}
                        </p>
                        <p
                          className={`text-[9.5px] mt-0.5 ${done && !wasCancelHere ? "text-[#5fbdb3]" : "text-[#c8d8d7]"}`}
                        >
                          {meta.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile tracker (vertical) */}
              <div className="flex sm:hidden flex-col gap-0">
                {BASE_STATUSES.map((status, i) => {
                  const meta = STATUS_META[status];
                  const done = i <= currentIdx;
                  const isLast = i === BASE_STATUSES.length - 1;
                  const wasCancelHere = isCancelled && i === currentIdx;

                  return (
                    <div key={status} className="flex gap-3">
                      {/* Line + circle column */}
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 border-[1.5px]
                          ${
                            wasCancelHere
                              ? "bg-red-500 border-red-500 text-white"
                              : done
                                ? "bg-[#1e4a46] border-[#1e4a46] text-white"
                                : "bg-white border-[#c8d8d7] text-[#aac9c7]"
                          }`}
                        >
                          {wasCancelHere ? (
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 14 14"
                              fill="white"
                            >
                              <path d="M9.43 7L13.5 2.94C14.17 2.26 14.17 1.18 13.5.51 12.82-.17 11.74-.17 11.07.51L7 4.57 2.93.5C2.26-.17 1.17-.17.5.5c-.67.67-.67 1.76 0 2.43L4.57 7 .5 11.07c-.67.67-.67 1.76 0 2.43.67.67 1.76.67 2.43 0L7 9.43l4.07 4.07c.67.67 1.76.67 2.43 0 .67-.67.67-1.76 0-2.43L9.43 7z" />
                            </svg>
                          ) : done ? (
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="white"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <span className="text-[13px]">{meta.icon}</span>
                          )}
                        </div>
                        {!isLast && (
                          <div
                            className={`w-[2px] h-8 ${done ? "bg-[#1e4a46]" : "bg-[#dde9e8]"}`}
                          />
                        )}
                      </div>
                      {/* Text */}
                      <div className="pb-6">
                        <p
                          className={`text-[13px] font-bold ${done && !wasCancelHere ? "text-[#1e4a46]" : wasCancelHere ? "text-red-500" : "text-[#aac9c7]"}`}
                        >
                          {meta.label}
                        </p>
                        <p
                          className={`text-[11px] ${done && !wasCancelHere ? "text-[#5a7a77]" : "text-[#c8d8d7]"}`}
                        >
                          {meta.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Order Items ── */}
          <div
            className="bg-white rounded-2xl border-[1.5px] border-[#c8d8d7] overflow-hidden"
            style={{ boxShadow: "0 2px 16px rgba(22,44,42,0.07)" }}
          >
            <div className="bg-[#f2f8f7] border-b border-[#c8d8d7] px-5 py-3.5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-[8px] bg-[#ddf0ee] border border-[#aac9c7] flex items-center justify-center flex-shrink-0">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1e4a46"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="9" cy="21" r="1" />
                    <circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                </div>
                <span className="text-[14px] font-black text-[#1e4a46]">
                  Order Items
                </span>
              </div>
              {products.length > 0 && (
                <span className="text-[12px] font-bold text-[#1e4a46] bg-[#ddf0ee] border border-[#aac9c7] rounded-full px-2.5 py-0.5">
                  {products.length} item{products.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            <div className="p-4 flex flex-col gap-3">
              {loading ? (
                Array.from({ length: 2 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex gap-3 p-3 bg-[#f2f8f7] rounded-xl animate-pulse"
                  >
                    <div className="w-[70px] h-[70px] rounded-xl bg-[#dde9e8] flex-shrink-0" />
                    <div className="flex-1 flex flex-col gap-2 justify-center">
                      <div className="h-3 bg-[#dde9e8] rounded-full w-3/4" />
                      <div className="h-3 bg-[#dde9e8] rounded-full w-1/2" />
                      <div className="h-3 bg-[#dde9e8] rounded-full w-1/3" />
                    </div>
                  </div>
                ))
              ) : products.length === 0 ? (
                <p className="text-[14px] text-[#5a7a77] text-center py-8">
                  No items found for this order.
                </p>
              ) : (
                products.map((item, i) => {
                  const mrp = getMrp(item?.variant);
                  const final = getFinal(item?.variant);
                  const qty = item?.quantity || 1;
                  const hasDiscount = final < mrp;
                  const isOrange = i % 2 === 1;

                  return (
                    <div
                      key={i}
                      onClick={() =>
                        handleProductClick(
                          item.product_id,
                          item?.variant?.product?.name,
                        )
                      }
                      className={`flex gap-3.5 p-3.5 rounded-2xl border-[1.5px] cursor-pointer transition-all duration-200 hover:-translate-y-0.5 group
                        ${isOrange ? "bg-[#fff8f5] border-[#ffd5c2] hover:border-[#ff601e]" : "bg-[#f2f8f7] border-[#c8d8d7] hover:border-[#5fbdb3]"}`}
                      style={{
                        boxShadow: isOrange
                          ? "0 2px 8px rgba(255,96,30,0.06)"
                          : "0 2px 8px rgba(95,189,179,0.06)",
                      }}
                    >
                      {/* Image */}
                      <div
                        className={`w-[72px] h-[72px] min-w-[72px] rounded-[12px] border flex items-center justify-center overflow-hidden bg-white flex-shrink-0
                        ${isOrange ? "border-[#ffd5c2]" : "border-[#c8d8d7]"}`}
                      >
                        <img
                          src={getImage(item)}
                          alt={item?.variant?.product?.name || "Product"}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/default.svg";
                          }}
                          className="w-12 h-12 object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0 flex flex-col gap-1">
                        <p
                          className={`text-[14px] font-black leading-snug line-clamp-2 group-hover:text-[#1e4a46] transition-colors ${isOrange ? "text-[#1e3330]" : "text-[#1e3330]"}`}
                          style={{ fontFamily: "'Nunito',sans-serif" }}
                        >
                          {item?.variant?.product?.name || "Unknown Product"}
                        </p>
                        {item?.variant?.pack_size && (
                          <p className="text-[12px] text-[#5a7a77] font-medium">
                            {item.variant.pack_size}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {hasDiscount && (
                            <span className="text-[12px] text-[#aac9c7] line-through">
                              ₹{(mrp * qty).toFixed(0)}
                            </span>
                          )}
                          <span
                            className="text-[15px] font-black text-[#1e4a46]"
                            style={{ fontFamily: "'Nunito',sans-serif" }}
                          >
                            ₹{(final * qty).toFixed(0)}
                          </span>
                          {hasDiscount && (
                            <span
                              className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full border
                              ${isOrange ? "bg-[#fff3ee] text-[#ff601e] border-[#ffd5c2]" : "bg-[#ddf0ee] text-[#1e4a46] border-[#aac9c7]"}`}
                            >
                              {Math.round(((mrp - final) / mrp) * 100)}% off
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Qty badge */}
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center font-black text-[14px] self-center border
                        ${isOrange ? "bg-[#fff3ee] text-[#ff601e] border-[#ffd5c2]" : "bg-[#ddf0ee] text-[#1e4a46] border-[#aac9c7]"}`}
                        style={{ fontFamily: "'Nunito',sans-serif" }}
                      >
                        ×{qty}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ── Cancel Order ── */}
          {canCancel && !loading && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 h-11 rounded-xl border-[1.5px] border-[#ffd5c2] bg-white text-[#ff601e] text-[14px] font-bold hover:bg-[#fff3ee] hover:border-[#ff601e] transition-all duration-200 cursor-pointer"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              Cancel Order
            </button>
          )}
        </div>

        {/* ════ RIGHT COLUMN ════ */}
        <div className="flex flex-col gap-5">
          {/* ── Shipment Info ── */}
          <div
            className="bg-white rounded-2xl border-[1.5px] border-[#c8d8d7] overflow-hidden"
            style={{ boxShadow: "0 2px 16px rgba(22,44,42,0.07)" }}
          >
            <div className="bg-[#f2f8f7] border-b border-[#c8d8d7] px-5 py-3.5 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-[8px] bg-[#ddf0ee] border border-[#aac9c7] flex items-center justify-center flex-shrink-0">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1e4a46"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <span className="text-[14px] font-black text-[#1e4a46]">
                Shipment Details
              </span>
            </div>
            <div className="px-5 py-4">
              <InfoRow label="Order ID" value={order?.orderId} mono />
              <InfoRow label="Placed On" value={fmt(order?.created_at)} />
              <InfoRow
                label="Last Updated"
                value={`${fmt(order?.updated_at)} ${fmtTime(order?.updated_at)}`}
              />
              <InfoRow label="Payment Type" value={order?.payment_type} />
              <InfoRow
                label="Delivery Type"
                value={order?.delivery_type?.replace(/_/g, " ")}
              />
            </div>
          </div>

          {/* ── Bill Summary ── */}
          <div
            className="bg-white rounded-2xl border-[1.5px] border-[#c8d8d7] overflow-hidden"
            style={{ boxShadow: "0 2px 16px rgba(22,44,42,0.07)" }}
          >
            <div className="bg-[#f2f8f7] border-b border-[#c8d8d7] px-5 py-3.5 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-[8px] bg-[#ddf0ee] border border-[#aac9c7] flex items-center justify-center flex-shrink-0">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1e4a46"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </div>
              <span className="text-[14px] font-black text-[#1e4a46]">
                Bill Summary
              </span>
            </div>
            <div className="px-5 py-4">
              {order?.bill?.length > 0 ? (
                order.bill.map((b, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <div className="flex justify-between items-center py-1.5 border-b border-[#f1f5f5]">
                      <span className="text-[13px] text-[#5a7a77] font-medium">
                        Total MRP
                      </span>
                      <span className="text-[14px] font-bold text-[#1e3330]">
                        ₹{b.mrp}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-b border-[#f1f5f5]">
                      <span className="text-[13px] text-[#5a7a77] font-medium">
                        Discount
                      </span>
                      <span className="text-[14px] font-bold text-[#059669]">
                        − ₹{b.discount}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-b border-[#f1f5f5]">
                      <span className="text-[13px] text-[#5a7a77] font-medium">
                        Delivery
                      </span>
                      <span className="text-[14px] font-bold text-[#059669]">
                        Free
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 mt-1">
                      <span className="text-[15px] font-black text-[#0a1715]">
                        Amount Paid
                      </span>
                      <span
                        className="text-[19px] font-black text-[#1e4a46]"
                        style={{ fontFamily: "'Nunito',sans-serif" }}
                      >
                        ₹{b.totalAmount}
                      </span>
                    </div>
                    {/* Savings pill */}
                    {parseFloat(b.discount) > 0 && (
                      <div className="flex items-center gap-2 bg-[#ddf0ee] border border-[#aac9c7] rounded-xl px-3.5 py-2.5 mt-1">
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#1e4a46"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span className="text-[13px] font-bold text-[#1e4a46]">
                          You saved ₹{b.discount} on this order!
                        </span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-[14px] text-[#5a7a77] py-4 text-center">
                  Bill details not available.
                </p>
              )}
            </div>
          </div>

          {/* ── Help ── */}
          <div
            className="bg-white rounded-2xl border-[1.5px] border-[#c8d8d7] overflow-hidden"
            style={{ boxShadow: "0 2px 16px rgba(22,44,42,0.07)" }}
          >
            <div className="p-5 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#ddf0ee] border border-[#aac9c7] flex items-center justify-center flex-shrink-0">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1e4a46"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div>
                  <p className="text-[14px] font-black text-[#1e4a46]">
                    Need Help?
                  </p>
                  <p className="text-[12px] text-[#5a7a77]">
                    Our support team is here for you
                  </p>
                </div>
              </div>
              <Link to="/faq" style={{ textDecoration: "none" }}>
                <button className="w-full h-10 rounded-xl border-[1.5px] border-[#aac9c7] bg-[#f2f8f7] text-[#1e4a46] text-[13px] font-bold flex items-center justify-center gap-2 hover:bg-[#ddf0ee] hover:border-[#5fbdb3] transition-all duration-200 cursor-pointer">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  FAQs & Support
                </button>
              </Link>
              <Link to="/contact-us" style={{ textDecoration: "none" }}>
                <button className="w-full h-10 rounded-xl bg-[#1e4a46] hover:bg-[#ff601e] text-white text-[13px] font-bold flex items-center justify-center gap-2 transition-all duration-200 border-none cursor-pointer">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.72 12 19.79 19.79 0 0 1 1.65 3.4 2 2 0 0 1 3.62 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  Contact Support
                </button>
              </Link>
            </div>
          </div>

          {/* Secure badge */}
        </div>
      </div>

      {/* ════ CANCEL MODAL ════ */}
      {isModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm font-poppins p-4"
          style={{ zIndex: 99999 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[420px] overflow-hidden">
            <div className="h-[3px] bg-gradient-to-r from-[#1e4a46] via-[#5fbdb3] to-[#ff601e]" />
            <div className="px-6 pt-5 pb-2 flex items-center justify-between border-b border-[#f1f5f5]">
              <div>
                <h2
                  className="text-[17px] font-black text-[#1e4a46]"
                  style={{ fontFamily: "'Nunito',sans-serif" }}
                >
                  Cancel Order
                </h2>
                <p className="text-[13px] text-[#5a7a77] mt-0.5">
                  Please tell us why you're cancelling
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-xl border border-[#c8d8d7] flex items-center justify-center hover:border-[#ff601e] hover:text-[#ff601e] transition-all cursor-pointer bg-white"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
              {CANCEL_REASONS.map((reason) => (
                <label
                  key={reason}
                  onClick={() => {
                    setSelectedReason(reason);
                    setCancelError("");
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border-[1.5px] cursor-pointer transition-all duration-200
                    ${selectedReason === reason ? "bg-[#ddf0ee] border-[#aac9c7]" : "bg-[#f2f8f7] border-[#c8d8d7] hover:border-[#5fbdb3]"}`}
                >
                  <div
                    className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0 transition-all
                    ${selectedReason === reason ? "border-[#1e4a46] bg-[#1e4a46]" : "border-[#aac9c7] bg-white"}`}
                  >
                    {selectedReason === reason && (
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    )}
                  </div>
                  <span
                    className={`text-[12.5px] font-medium ${selectedReason === reason ? "text-[#1e4a46] font-bold" : "text-[#2d5250]"}`}
                  >
                    {reason}
                  </span>
                </label>
              ))}
              {cancelError && (
                <p className="text-[12px] text-red-500 font-medium mt-1 flex items-center gap-1">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {cancelError}
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#f1f5f5] flex gap-3">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setCancelError("");
                  setSelectedReason("");
                }}
                className="flex-1 h-11 rounded-xl border-[1.5px] border-[#c8d8d7] text-[#2d5250] text-[14px] font-bold hover:border-[#5fbdb3] transition-all cursor-pointer bg-white"
              >
                Keep Order
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1 h-11 rounded-xl bg-red-500 hover:bg-red-600 text-white text-[14px] font-bold flex items-center justify-center gap-2 transition-all border-none cursor-pointer disabled:opacity-60"
              >
                {cancelling && (
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                )}
                {cancelling ? "Cancelling…" : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes stepPing {
          0%   { transform:scale(1);   opacity:.2; }
          70%  { transform:scale(1.8); opacity:0;  }
          100% { transform:scale(1.8); opacity:0;  }
        }
        @keyframes spin {
          from { transform:rotate(0deg); } to { transform:rotate(360deg); }
        }
        .animate-spin { animation: spin 1s linear infinite; }
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@800;900&family=Poppins:wght@400;500;600;700&display=swap');
      `}</style>
    </div>
  );
};

export default Orderdetails;
